import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Sparkles, Share2, Download, Check, Loader2, Clock, Repeat, Play } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import AudioEffectsManager from '../utils/AudioEffectsManager';
import ShareMomentDialog from './ShareMomentDialog';

import { isSpanishTranslation } from '../utils/bookTranslations';
import useBedtime from './audio-bible-player/useBedtime';
import { BedtimeToggle, ResumeBanner } from './audio-bible-player/BedtimeControls';
import TransportControls from './audio-bible-player/TransportControls';
import SpecialEffectsPanel from './audio-bible-player/SpecialEffectsPanel';
import AmbientScenePanel from './audio-bible-player/AmbientScenePanel';
import NarrationSettings from './audio-bible-player/NarrationSettings';

const API = process.env.REACT_APP_BACKEND_URL;
const AUDIO_CACHE = 'lumera-audio-v1';

/**
 * Lumera Immersive Audio Bible Player.
 *
 * Narration (browser TTS or OpenAI premium) + layered ambient soundscapes
 * in two modes: auto-detect from verse text, or a curated scene preset.
 *
 * Owns all playback state and narration logic; delegates presentation to
 * the dedicated subcomponents inside `./audio-bible-player/`.
 */
const AudioBiblePlayer = ({ isOpen, onClose, verse, chapterVerses = [], translation, initialScope = 'verse', onRequestNextChapter, nextChapterToken = 0 }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const uiEs = language === 'es';
  // Authoritative premium check — the user object doesn't carry subscription
  // status, so fetch it when the player opens. Regular (browser) narration is
  // free for everyone; ambient scenes, effects and the OpenAI voice are Premium.
  const [isPremium, setIsPremium] = useState(user?.role === 'super_admin');
  useEffect(() => {
    if (!isOpen) return;
    if (user?.role === 'super_admin') { setIsPremium(true); return; }
    let cancelled = false;
    fetch(`${API}/api/payments/subscription`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        const planId = d?.plan?.id;
        setIsPremium(!!d?.subscription && planId && planId !== 'free');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, user]);
  const hasChapter = Array.isArray(chapterVerses) && chapterVerses.length > 1;

  // Reading scope: 'verse' (single) or 'chapter' (full)
  const [scope, setScope] = useState(initialScope === 'chapter' ? 'chapter' : 'verse');
  const [chapterIdx, setChapterIdx] = useState(0);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingTts, setIsLoadingTts] = useState(false);
  const [progress, setProgress] = useState(0);

  // Narration settings (persisted across sessions)
  const [narrationSource, setNarrationSource] = useState(() => localStorage.getItem('lumera_narration_source') || 'browser');
  const [volume, setVolume] = useState(1);
  const [rate, setRate] = useState(() => parseFloat(localStorage.getItem('lumera_narration_rate')) || 1);
  const [voice, setVoice] = useState(null);
  const [voices, setVoices] = useState([]);
  const [openAiVoice, setOpenAiVoice] = useState('nova');

  // Special effects (keyword-triggered during narration)
  const [effectsEnabled, setEffectsEnabled] = useState(true);
  const [effectVolume, setEffectVolume] = useState(0.3);
  const [liveSync, setLiveSync] = useState(true);
  const [activeEffects, setActiveEffects] = useState([]);

  // Ambient scene (standalone looping soundscape)
  const [sceneId, setSceneId] = useState('wilderness');
  const [sceneVolume, setSceneVolume] = useState(0.4);
  const [scenePlaying, setScenePlaying] = useState(false);
  const [activeSceneLayers, setActiveSceneLayers] = useState([]);

  // Shared premium-samples toggle — applies to both effect + scene managers
  const [useRealSamples, setUseRealSamples] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Bedtime mode flag — the hook handles bookmarking + auto-fade.
  const [bedtimeMode, setBedtimeMode] = useState(false);

  // Auto-advance into the next chapter when the current one finishes.
  const [autoplayNext, setAutoplayNext] = useState(
    () => localStorage.getItem('lumera_autoplay_next_chapter') === '1',
  );
  const autoplayArmRef = useRef(0);
  // Sleep timer: minutes until playback auto-stops (0 = off).
  const [sleepMinutes, setSleepMinutes] = useState(0);
  const sleepTimerRef = useRef(null);

  // Refs
  const utteranceRef = useRef(null);
  const synthRef = useRef(typeof window !== 'undefined' ? window.speechSynthesis : null);
  const audioElRef = useRef(null);
  const stopAllRef = useRef(null);
  const effectsManagerRef = useRef(null);
  const isOpenRef = useRef(isOpen);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  const sceneManagerRef = useRef(null);
  if (typeof window !== 'undefined') {
    if (!effectsManagerRef.current) effectsManagerRef.current = new AudioEffectsManager();
    if (!sceneManagerRef.current) sceneManagerRef.current = new AudioEffectsManager();
  }

  // Compute the active verse based on scope
  const activeVerse = useMemo(() => {
    if (scope === 'chapter' && hasChapter && chapterVerses[chapterIdx]) {
      const v = chapterVerses[chapterIdx];
      return {
        text: v.text || v.verse || '',
        reference: v.reference || v.verse_reference || '',
        translation: v.translation || translation || verse?.translation || '',
      };
    }
    if (verse) {
      return { ...verse, translation: verse.translation || translation || '' };
    }
    return verse;
  }, [scope, chapterIdx, chapterVerses, verse, hasChapter, translation]);

  const isSpanish = useMemo(
    () => {
      const t = activeVerse?.translation || translation || '';
      // Use the shared, case-insensitive helper so ALL Spanish translations
      // (RVR1909, RVG, ONBV, ESS, PDPT, BLM, VBL, …) narrate with a Spanish voice.
      return isSpanishTranslation(t);
    },
    [activeVerse, translation],
  );

  // Parse "Book Chapter:Verse" (e.g. "1 Corinthians 13:4") from the reference.
  const parseBookChapter = () => {
    const ref = activeVerse?.reference || verse?.reference || '';
    const m = ref.match(/^(.+?)\s+(\d+):\d+/);
    if (!m) return null;
    return { book: m[1].trim(), chapter: parseInt(m[2], 10) };
  };

  // Human (public-domain KJV) narration is English-only and needs a parseable ref.
  const humanAudioAvailable = useMemo(
    () => !isSpanish && !!parseBookChapter(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeVerse, verse, isSpanish],
  );

  // If the human voice is selected but becomes unavailable, drop back to browser.
  useEffect(() => {
    if (narrationSource === 'human' && !humanAudioAvailable) {
      setNarrationSource('browser');
    }
  }, [narrationSource, humanAudioAvailable]);

  // Offline audio download state for the current chapter's human narration.
  const [offlineState, setOfflineState] = useState('idle'); // idle | saving | saved
  const mp3UrlRef = useRef(null);

  const resolveMp3Url = async () => {
    const bc = parseBookChapter();
    if (!bc) return null;
    const res = await fetch(
      `${API}/api/bible/audio-url?book=${encodeURIComponent(bc.book)}`
        + `&chapter=${bc.chapter}&translation=KJV`,
      { credentials: 'include' },
    );
    if (!res.ok) return null;
    const data = await res.json();
    mp3UrlRef.current = data?.url || null;
    return mp3UrlRef.current;
  };

  // Reflect whether the current chapter is already saved offline.
  const bookChapterKey = useMemo(() => {
    const bc = parseBookChapter();
    return bc ? `${bc.book}|${bc.chapter}` : '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVerse, verse]);

  useEffect(() => {
    if (!isOpen || !humanAudioAvailable || typeof caches === 'undefined') {
      setOfflineState('idle');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const mp3 = await resolveMp3Url();
        if (cancelled || !mp3) return;
        const cache = await caches.open(AUDIO_CACHE);
        const hit = await cache.match(mp3, { ignoreVary: true });
        if (!cancelled) setOfflineState(hit ? 'saved' : 'idle');
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, humanAudioAvailable, bookChapterKey]);

  const handleSaveOffline = async () => {
    if (!user) {
      toast.error(uiEs ? 'Inicia sesión para guardar audio sin conexión.' : 'Sign in to save audio for offline listening.');
      return;
    }
    if (typeof caches === 'undefined') {
      toast.error(uiEs ? 'El guardado sin conexión no es compatible con este navegador.' : 'Offline saving is not supported in this browser.');
      return;
    }
    const bc = parseBookChapter();
    if (!bc) return;
    setOfflineState('saving');
    try {
      const mp3 = mp3UrlRef.current || (await resolveMp3Url());
      if (!mp3) throw new Error('No audio URL');
      const resp = await fetch(mp3, { mode: 'no-cors' });
      const cache = await caches.open(AUDIO_CACHE);
      await cache.put(mp3, resp);
      setOfflineState('saved');
      toast.success(uiEs ? `Se guardó ${bc.book} ${bc.chapter} para escuchar sin conexión` : `Saved ${bc.book} ${bc.chapter} for offline listening`);
    } catch (e) {
      console.error('Offline audio save failed:', e);
      setOfflineState('idle');
      toast.error(uiEs ? 'No se pudo guardar este capítulo sin conexión' : 'Could not save this chapter for offline');
    }
  };

  // Persist narration source & rate whenever they change.
  useEffect(() => { localStorage.setItem('lumera_narration_source', narrationSource); }, [narrationSource]);
  useEffect(() => { localStorage.setItem('lumera_narration_rate', String(rate)); }, [rate]);
  useEffect(() => { localStorage.setItem('lumera_autoplay_next_chapter', autoplayNext ? '1' : '0'); }, [autoplayNext]);

  // Sleep timer — hard-stop playback after the chosen number of minutes.
  useEffect(() => {
    if (sleepTimerRef.current) { clearTimeout(sleepTimerRef.current); sleepTimerRef.current = null; }
    if (sleepMinutes > 0) {
      sleepTimerRef.current = setTimeout(() => {
        stopAllRef.current?.();
        setSleepMinutes(0);
        toast.info(uiEs ? 'Temporizador de sueño: reproducción detenida 🌙' : 'Sleep timer: playback stopped 🌙');
      }, sleepMinutes * 60 * 1000);
    }
    return () => { if (sleepTimerRef.current) { clearTimeout(sleepTimerRef.current); sleepTimerRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleepMinutes]);

  // Premium narration: restore the reader's saved premium voice for this
  // language (Spanish readers can pick & keep their preferred Spanish voice).
  // Falls back to 'nova' and, for premium Spanish users on first use, defaults
  // the engine to OpenAI for the highest-quality Spanish narration.
  useEffect(() => {
    if (!isOpen) return;
    const langKey = isSpanish ? 'es' : 'en';
    const savedVoice = localStorage.getItem(`lumera_tts_openai_voice_${langKey}`);
    if (savedVoice) setOpenAiVoice(savedVoice);
    else if (isPremium && isSpanish) setOpenAiVoice('coral');
    if (isPremium && isSpanish && !localStorage.getItem('lumera_narration_source')) {
      setNarrationSource('openai');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isPremium, isSpanish]);

  // Persist the premium voice choice per language so it's remembered.
  const handleOpenAiVoiceChange = (v) => {
    setOpenAiVoice(v);
    localStorage.setItem(`lumera_tts_openai_voice_${isSpanish ? 'es' : 'en'}`, v);
  };
  const handleBrowserVoiceChange = (v) => {
    setVoice(v);
    if (v?.name) localStorage.setItem(`lumera_tts_browser_voice_${isSpanish ? 'es' : 'en'}`, v.name);
  };

  /* -------------------- load browser voices -------------------- */
  useEffect(() => {
    if (!synthRef.current) return;
    const loadVoices = () => {
      const available = synthRef.current.getVoices();
      setVoices(available);
      const langCode = isSpanish ? 'es' : 'en';
      const savedName = localStorage.getItem(`lumera_tts_browser_voice_${langCode}`);
      const preferred =
        (savedName && available.find((v) => v.name === savedName)) ||
        available.find((v) => v.lang.startsWith(langCode) && v.name.includes('Google')) ||
        available.find((v) => v.lang.startsWith(langCode)) ||
        available[0];
      if (preferred) setVoice(preferred);
    };
    loadVoices();
    if (synthRef.current.onvoiceschanged !== undefined) {
      synthRef.current.onvoiceschanged = loadVoices;
    }
  }, [isSpanish]);

  /* -------------------- sync effects settings -------------------- */
  useEffect(() => {
    effectsManagerRef.current?.setEffectsEnabled(effectsEnabled);
    effectsManagerRef.current?.setEffectVolume(effectVolume);
  }, [effectsEnabled, effectVolume]);

  useEffect(() => {
    sceneManagerRef.current?.setEffectVolume(sceneVolume);
  }, [sceneVolume]);

  useEffect(() => {
    const real = useRealSamples && isPremium;
    effectsManagerRef.current?.setUseRealSamples(real);
    sceneManagerRef.current?.setUseRealSamples(real);
  }, [useRealSamples, isPremium]);

  /* -------------------- cleanup on close/unmount -------------------- */
  const stopAll = () => {
    try { synthRef.current?.cancel(); } catch (_) { /* ignore */ }
    if (audioElRef.current) {
      try {
        audioElRef.current.pause();
        // Fully detach the source. Setting src='' leaves the element pointing
        // at the page URL (a truthy but unplayable src), so remove the
        // attribute and reload to reset to a clean, empty media element.
        audioElRef.current.removeAttribute('src');
        audioElRef.current.load();
      } catch (_) { /* ignore */ }
    }
    effectsManagerRef.current?.stopAllEffects();
    sceneManagerRef.current?.stopAllEffects();
    setActiveEffects([]);
    setActiveSceneLayers([]);
    setScenePlaying(false);
    setIsPlaying(false);
    setProgress(0);
  };
  stopAllRef.current = stopAll;

  useEffect(() => {
    if (!isOpen) stopAll();
    return () => stopAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Reset playback when switching narration engines so a stale blob/revoked URL
  // from the previous engine is never resumed by accident (prevents a mid-read
  // engine switch from leaving a dead audio element).
  const prevSourceRef = useRef(narrationSource);
  useEffect(() => {
    if (prevSourceRef.current !== narrationSource) {
      prevSourceRef.current = narrationSource;
      stopAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrationSource]);

  // Auto-play next verse in chapter mode.
  useEffect(() => {
    if (scope !== 'chapter' || !isPlaying || !hasChapter) return;
    if (narrationSource === 'human') return; // human audio is one file per chapter
    setProgress(0);
    const t = setTimeout(() => {
      if (!isOpenRef.current) return;
      if (narrationSource === 'openai') speakWithOpenAi();
      else speakWithBrowser();
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterIdx]);

  // Reset chapter index when scope switches or the dialog opens
  useEffect(() => {
    setChapterIdx(0);
    if (scope === 'chapter' && hasChapter && verse) {
      const i = chapterVerses.findIndex(
        (v) => (v.reference || v.verse_reference) === verse.reference,
      );
      if (i >= 0) setChapterIdx(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, isOpen]);

  /* -------------------- bedtime companion hook -------------------- */
  const { resumeBookmark, setResumeBookmark, clearFade } = useBedtime({
    isOpen,
    user,
    hasChapter,
    verse,
    activeVerse,
    chapterIdx,
    scope,
    bedtimeMode,
    isPlaying,
    volume,
    sceneVolume,
    onVolumeChange: setVolume,
    onSceneVolumeChange: setSceneVolume,
  });

  const enableBedtime = () => {
    setBedtimeMode(true);
    setScope('chapter');
    setSceneId('night');
    setScenePlaying(true); // standalone night scene loop
    setRate(0.85);
  };
  const disableBedtime = () => {
    setBedtimeMode(false);
    clearFade();
  };

  const handleResume = () => {
    if (!resumeBookmark) return;
    const idx = Number.isInteger(resumeBookmark.chapter_idx) ? resumeBookmark.chapter_idx : 0;
    setScope('chapter');
    setChapterIdx(Math.min(Math.max(0, idx), Math.max(0, chapterVerses.length - 1)));
    setResumeBookmark(null);
    toast.success(uiEs ? `Reanudando desde ${resumeBookmark.verse_reference}` : `Resuming from ${resumeBookmark.verse_reference}`);
  };
  const handleDismissResume = () => setResumeBookmark(null);

  /* -------------------- special-effects start (TTS-synced) -------------------- */
  const startAmbient = () => {
    if (!effectsEnabled || !activeVerse || !isOpenRef.current) {
      setActiveEffects([]);
      return;
    }
    // Play the verse's detected effects IMMEDIATELY so ambience is audible even
    // when TTS word-boundary events (which drive live-sync) never fire — notably
    // on iOS Safari/Chrome. This also resumes a suspended AudioContext within the
    // play gesture. Live-sync still refines/adds layers as narration proceeds.
    const layers = effectsManagerRef.current?.playEffectsForVerse(activeVerse.text, 0) || [];
    setActiveEffects(layers);
  };

  const syncLayersUpToChar = (charIndex) => {
    if (!activeVerse || !liveSync || !effectsEnabled) return;
    const windowText = activeVerse.text.slice(0, Math.min(activeVerse.text.length, charIndex + 40));
    const detected = effectsManagerRef.current?.analyzeText(windowText) || [];
    const desired = detected.slice(-3);
    const active = effectsManagerRef.current?.syncLayers(desired, 3) || [];
    setActiveEffects(active);
  };

  /* -------------------- ambient scene (standalone) -------------------- */
  const toggleScenePlayback = () => {
    if (scenePlaying) {
      sceneManagerRef.current?.stopAllEffects();
      setActiveSceneLayers([]);
      setScenePlaying(false);
      return;
    }
    const mgr = sceneManagerRef.current;
    if (!mgr) return;
    mgr.setEffectsEnabled(true); // scene channel stays always-enabled
    mgr.setEffectVolume(sceneVolume);
    const layers = mgr.playScene(sceneId) || [];
    setActiveSceneLayers(layers);
    setScenePlaying(true);
  };

  // If the scene preset changes while playing, switch the loop live.
  useEffect(() => {
    if (!scenePlaying) return;
    const mgr = sceneManagerRef.current;
    if (!mgr) return;
    const layers = mgr.playScene(sceneId) || [];
    setActiveSceneLayers(layers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneId]);

  const advanceChapter = () => {
    if (scope !== 'chapter' || !hasChapter) return false;
    if (chapterIdx < chapterVerses.length - 1) {
      setChapterIdx((i) => i + 1);
      return true;
    }
    return false;
  };

  // Called when the current chapter reaches its end. If "autoplay next chapter"
  // is on, ask the parent to load the next chapter and keep playing; returns
  // true when a next chapter was requested (so the caller shouldn't stop).
  const requestNextChapterIfAuto = () => {
    if (scope !== 'chapter' || !autoplayNext || typeof onRequestNextChapter !== 'function') {
      return false;
    }
    const advancing = onRequestNextChapter();
    if (advancing === false) {
      toast.info(uiEs ? 'Fin del libro' : 'End of book');
      return false; // end of book — let it stop
    }
    autoplayArmRef.current = Date.now(); // arm auto-start once new verses arrive
    setProgress(0);
    return true;
  };

  // Parent bumps nextChapterToken once the next chapter's verses have loaded.
  // Reset to verse 1 of the new chapter in full-chapter scope.
  useEffect(() => {
    if (!nextChapterToken || !autoplayArmRef.current) return;
    setScope('chapter');
    setChapterIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextChapterToken]);

  // Once the new chapter's first verse is ready (activeVerse recomputed), start
  // playing it. Guarded by autoplayArmRef so normal verse advances never retrigger.
  useEffect(() => {
    if (!autoplayArmRef.current || !activeVerse) return;
    autoplayArmRef.current = 0;
    const t = setTimeout(() => play(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextChapterToken, activeVerse]);

  // Fire a "share to your story" milestone nudge when a chapter finishes.
  const dispatchChapterMilestone = () => {
    const ref = verse?.reference ? verse.reference.replace(/:\d+.*$/, '') : '';
    window.dispatchEvent(new CustomEvent('lumera:milestone-share', { detail: {
      kind: 'audio_chapter',
      reference: ref,
      verseText: '',
      message: ref ? `You finished listening to ${ref} 🎧` : 'Chapter finished 🎧',
    }}));
  };

  /* -------------------- browser TTS narration -------------------- */
  // Find the best installed voice for the target language from the full list,
  // rather than trusting the `voice` state (which may hold a stale/EN voice).
  const findVoiceForLang = (spanish) => {
    const code = spanish ? 'es' : 'en';
    if (voice && voice.lang && voice.lang.toLowerCase().startsWith(code)) return voice;
    return (
      voices.find((v) => v.lang.toLowerCase().startsWith(code) && /google/i.test(v.name)) ||
      voices.find((v) => v.lang.toLowerCase().startsWith(code) && /(microsoft|natural|premium|enhanced)/i.test(v.name)) ||
      voices.find((v) => v.lang.toLowerCase().startsWith(code)) ||
      null
    );
  };

  const speakWithBrowser = () => {
    if (!synthRef.current || !activeVerse) return;

    // Pick a voice that actually matches the passage language. Reading Spanish
    // with an English voice produces the "English accent" artefact the reader hears.
    const chosen = findVoiceForLang(isSpanish);

    // No native Spanish voice installed on this device: browser TTS literally
    // cannot pronounce Spanish natively. Premium readers get auto-upgraded to
    // the AI voice; free readers are told why.
    if (isSpanish && !chosen) {
      if (isPremium) {
        toast.info(uiEs
          ? 'Tu dispositivo no tiene una voz en español — usando la voz AI para una narración nativa.'
          : 'Your device has no Spanish voice — switching to the AI voice for native Spanish narration.');
        setNarrationSource('openai');
        return speakWithOpenAi();
      }
      toast.warning(uiEs
        ? 'Tu dispositivo no tiene una voz en español instalada, así que la narración puede sonar con acento. Instala una voz en español o mejora a Premium para la voz AI nativa.'
        : 'Your device has no Spanish voice installed, so narration may sound accented. Install a Spanish voice or upgrade to Premium for the native AI voice.');
    }

    synthRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(activeVerse.text);
    // Assign the matched voice when we have one; always hint the language.
    if (chosen) {
      utterance.voice = chosen;
      utterance.lang = chosen.lang;
    } else {
      utterance.lang = isSpanish ? 'es-ES' : 'en-US';
    }
    utterance.rate = rate;
    utterance.volume = volume;
    utterance.pitch = 1;

    utterance.onstart = () => setIsPlaying(true);
    utterance.onend = () => {
      if (scope === 'chapter' && advanceChapter()) return;
      if (requestNextChapterIfAuto()) return;
      effectsManagerRef.current?.stopAllEffects();
      setActiveEffects([]);
      setIsPlaying(false);
      setProgress(100);
      toast.success(scope === 'chapter' ? (uiEs ? 'Capítulo terminado' : 'Finished reading chapter') : (uiEs ? 'Versículo terminado' : 'Finished reading verse'));
      if (scope === 'chapter') dispatchChapterMilestone();
    };
    utterance.onerror = (e) => {
      console.error('TTS error', e);
      effectsManagerRef.current?.stopAllEffects();
      setActiveEffects([]);
      setIsPlaying(false);
      toast.error(uiEs ? 'Error de reproducción de audio' : 'Audio playback error');
    };
    utterance.onboundary = (event) => {
      if (event.charIndex && activeVerse.text.length) {
        setProgress(Math.min(100, (event.charIndex / activeVerse.text.length) * 100));
      }
      syncLayersUpToChar(event.charIndex || 0);
    };

    utteranceRef.current = utterance;
    startAmbient();
    synthRef.current.speak(utterance);
  };

  /* -------------------- OpenAI TTS narration -------------------- */
  const speakWithOpenAi = async () => {
    if (!activeVerse) return;
    if (!isPremium) {
      toast.error(uiEs ? 'La voz premium es una función Premium — mejora tu plan para desbloquearla.' : 'The premium voice is a Premium feature — upgrade to unlock it.');
      setNarrationSource('browser');
      return speakWithBrowser();
    }
    setIsLoadingTts(true);
    try {
      const res = await fetch(`${API}/api/lumera/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: activeVerse.text,
          voice: openAiVoice,
          model: isSpanish ? 'tts-1-hd' : 'tts-1',
          speed: rate,
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const blob = await res.blob();
      if (!isOpenRef.current) return;  // player was closed mid-fetch — don't restart audio/effects
      const url = URL.createObjectURL(blob);

      if (!audioElRef.current) audioElRef.current = new Audio();
      const audio = audioElRef.current;
      audio.src = url;
      audio.volume = volume;

      audio.onplay = () => setIsPlaying(true);
      audio.onpause = () => { if (!audio.ended) setIsPlaying(false); };
      audio.ontimeupdate = () => {
        if (audio.duration) {
          const progressPct = (audio.currentTime / audio.duration) * 100;
          setProgress(progressPct);
          if (activeVerse) {
            const approxChar = Math.floor((audio.currentTime / audio.duration) * activeVerse.text.length);
            syncLayersUpToChar(approxChar);
          }
        }
      };
      audio.onended = () => {
        URL.revokeObjectURL(url);
        if (scope === 'chapter' && advanceChapter()) return;
        if (requestNextChapterIfAuto()) return;
        effectsManagerRef.current?.stopAllEffects();
        setActiveEffects([]);
        setIsPlaying(false);
        setProgress(100);
        toast.success(scope === 'chapter' ? (uiEs ? 'Capítulo terminado' : 'Finished reading chapter') : (uiEs ? 'Versículo terminado' : 'Finished reading verse'));
        if (scope === 'chapter') dispatchChapterMilestone();
      };
      audio.onerror = () => {
        effectsManagerRef.current?.stopAllEffects();
        setActiveEffects([]);
        setIsPlaying(false);
        toast.error(uiEs ? 'Error de reproducción de audio' : 'Audio playback error');
      };

      startAmbient();
      await audio.play();
    } catch (e) {
      console.error('OpenAI TTS failed:', e);
      toast.error(uiEs ? 'La voz premium falló — cambiando a la voz del navegador' : 'Premium voice failed — switching to browser voice');
      setNarrationSource('browser');
      speakWithBrowser();
    } finally {
      setIsLoadingTts(false);
    }
  };

  /* -------------------- human (public-domain KJV) narration -------------------- */
  const speakWithHuman = async () => {
    if (!user) {
      toast.error(uiEs ? 'Inicia sesión para escuchar la narración con voz humana.' : 'Sign in to listen to the human-voiced narration.');
      setNarrationSource('browser');
      return;
    }
    const bc = parseBookChapter();
    if (!bc) {
      toast.error(uiEs ? 'Narración humana no disponible aquí — usando la voz del navegador.' : 'Human narration unavailable here — using browser voice.');
      setNarrationSource('browser');
      return speakWithBrowser();
    }
    setIsLoadingTts(true);
    try {
      const url = `${API}/api/bible/audio-url?book=${encodeURIComponent(bc.book)}`
        + `&chapter=${bc.chapter}`
        + `&translation=${encodeURIComponent(activeVerse?.translation || translation || 'KJV')}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data?.url) throw new Error('No audio URL');

      if (!audioElRef.current) audioElRef.current = new Audio();
      const audio = audioElRef.current;
      audio.src = data.url;
      audio.volume = volume;
      audio.playbackRate = rate;

      audio.onplay = () => setIsPlaying(true);
      audio.onpause = () => { if (!audio.ended) setIsPlaying(false); };
      audio.ontimeupdate = () => {
        if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100);
      };
      audio.onended = () => {
        if (requestNextChapterIfAuto()) return;
        setIsPlaying(false);
        setProgress(100);
        toast.success(uiEs ? 'Narración del capítulo terminada' : 'Finished chapter narration');
        dispatchChapterMilestone();
      };
      audio.onerror = () => {
        setIsPlaying(false);
        toast.error(uiEs ? 'Error de reproducción — prueba la voz del navegador' : 'Audio playback error — try the browser voice');
      };

      toast.info(uiEs ? `Reproduciendo ${bc.book} ${bc.chapter} — ${data.narration || 'voz humana'} (capítulo completo)` : `Playing ${bc.book} ${bc.chapter} — ${data.narration || 'human voice'} (full chapter)`);
      await audio.play();
    } catch (e) {
      console.error('Human audio failed:', e);
      toast.error(uiEs ? 'Narración humana no disponible — cambiando a la voz del navegador' : 'Human narration unavailable — switching to browser voice');
      setNarrationSource('browser');
      speakWithBrowser();
    } finally {
      setIsLoadingTts(false);
    }
  };

  const play = () => {
    if (!activeVerse) return;
    effectsManagerRef.current?.unlock();  // resume AudioContext within the tap
    setProgress(0);
    if (narrationSource === 'human') speakWithHuman();
    else if (narrationSource === 'openai') speakWithOpenAi();
    else speakWithBrowser();
  };

  const handlePlayPause = () => {
    effectsManagerRef.current?.unlock();  // resume AudioContext within the tap (iOS)
    const usesAudioEl = narrationSource === 'openai' || narrationSource === 'human';
    if (isPlaying) {
      if (usesAudioEl && audioElRef.current) {
        audioElRef.current.pause();
      } else {
        synthRef.current?.pause();
      }
      setIsPlaying(false);
      effectsManagerRef.current?.stopAllEffects();
      setActiveEffects([]);
    } else if (
      usesAudioEl &&
      audioElRef.current &&
      !audioElRef.current.ended &&
      audioElRef.current.currentTime > 0 &&
      audioElRef.current.readyState >= 2
    ) {
      // Genuine resume of a track paused mid-playback. (A finished/empty/
      // revoked src would make .play() reject with "media resource not
      // suitable" — the uncaught error readers were hitting — so we only
      // resume a real live track and otherwise (re)start via play().)
      if (narrationSource === 'openai') startAmbient();
      Promise.resolve(audioElRef.current.play()).catch(() => play());
    } else if (synthRef.current?.paused && !usesAudioEl) {
      startAmbient();
      synthRef.current.resume();
      setIsPlaying(true);
    } else {
      play();
    }
  };

  const handleStop = () => stopAll();

  // Keep live audio element volume in sync
  useEffect(() => {
    if (audioElRef.current) audioElRef.current.volume = volume;
    if (utteranceRef.current) utteranceRef.current.volume = volume;
  }, [volume]);

  // Keep human/OpenAI audio playback rate in sync while playing.
  useEffect(() => {
    if (audioElRef.current) audioElRef.current.playbackRate = rate;
  }, [rate]);

  const scenes = effectsManagerRef.current?.getScenePresets() || [];

  if (!activeVerse) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="audio-bible-player">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/60 text-primary-foreground">
              <Sparkles className="w-4 h-4" />
            </span>
            {uiEs ? 'Lumera Audio Inmersivo' : 'Lumera Immersive Audio'}
          </DialogTitle>
          <DialogDescription>
            {uiEs ? 'Escucha las Escrituras con narración y ambiente en capas.' : 'Listen to scripture with narration and layered ambience.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {hasChapter && user && (
            <BedtimeToggle
              bedtimeMode={bedtimeMode}
              onEnable={enableBedtime}
              onDisable={disableBedtime}
              es={uiEs}
            />
          )}

          {resumeBookmark && (
            <ResumeBanner
              bookmark={resumeBookmark}
              onResume={handleResume}
              onDismiss={handleDismissResume}
              es={uiEs}
            />
          )}

          {hasChapter && (
            <Tabs value={scope} onValueChange={setScope}>
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="verse" data-testid="scope-verse">{uiEs ? 'Un versículo' : 'Single verse'}</TabsTrigger>
                <TabsTrigger value="chapter" data-testid="scope-chapter">
                  {uiEs ? 'Capítulo completo' : 'Full chapter'} ({chapterVerses.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* Single-verse mode: one-tap "keep reading" into the rest of the chapter */}
          {hasChapter && scope === 'verse' && (
            <Button
              variant="outline"
              className="w-full border-primary/40 text-primary hover:bg-primary/5"
              data-testid="continue-reading-chapter"
              onClick={() => { setScope('chapter'); setTimeout(() => play(), 250); }}
            >
              <Play className="w-4 h-4 mr-2" />
              {uiEs ? 'Continuar leyendo el capítulo' : 'Continue reading the chapter'}
            </Button>
          )}

          <TransportControls
            activeVerse={activeVerse}
            scope={scope}
            hasChapter={hasChapter}
            chapterIdx={chapterIdx}
            chapterVersesCount={chapterVerses.length}
            progress={progress}
            isPlaying={isPlaying}
            isLoadingTts={isLoadingTts}
            onPlayPause={handlePlayPause}
            onStop={handleStop}
            activeEffects={activeEffects}
            volume={volume}
            onVolumeChange={setVolume}
            es={uiEs}
          />

          {/* Listening options: auto-advance chapters + sleep timer */}
          <div className="rounded-lg border border-border/60 p-3 space-y-3" data-testid="listening-options">
            {hasChapter && (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Repeat className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-sm font-medium">{uiEs ? 'Reproducción continua' : 'Autoplay next chapter'}</p>
                    <p className="text-[11px] text-muted-foreground">{uiEs ? 'Continúa al siguiente capítulo automáticamente' : 'Roll into the next chapter hands-free'}</p>
                  </div>
                </div>
                <Switch
                  checked={autoplayNext}
                  onCheckedChange={setAutoplayNext}
                  data-testid="autoplay-next-toggle"
                />
              </div>
            )}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                <p className="text-sm font-medium">{uiEs ? 'Temporizador de sueño' : 'Sleep timer'}</p>
              </div>
              <div className="flex items-center gap-1">
                {[0, 15, 30, 45].map((m) => (
                  <Button
                    key={m}
                    size="sm"
                    variant={sleepMinutes === m ? 'default' : 'outline'}
                    className="h-7 px-2 text-[11px]"
                    onClick={() => setSleepMinutes(m)}
                    data-testid={`sleep-timer-${m}`}
                  >
                    {m === 0 ? (uiEs ? 'Apagado' : 'Off') : `${m}m`}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <SpecialEffectsPanel
            effectsEnabled={effectsEnabled}
            onToggleEffects={setEffectsEnabled}
            effectVolume={effectVolume}
            onEffectVolumeChange={setEffectVolume}
            liveSync={liveSync}
            onLiveSyncChange={setLiveSync}
            isPremium={isPremium}
            useRealSamples={useRealSamples}
            onUseRealSamplesChange={setUseRealSamples}
            es={uiEs}
          />

          <AmbientScenePanel
            sceneId={sceneId}
            onSceneChange={setSceneId}
            scenes={scenes}
            scenePlaying={scenePlaying}
            onTogglePlay={toggleScenePlayback}
            sceneVolume={sceneVolume}
            onSceneVolumeChange={setSceneVolume}
            activeSceneLayers={activeSceneLayers}
            es={uiEs}
          />

          <NarrationSettings
            narrationSource={narrationSource}
            onNarrationSourceChange={setNarrationSource}
            openAiVoice={openAiVoice}
            onOpenAiVoiceChange={handleOpenAiVoiceChange}
            voices={voices}
            voice={voice}
            onBrowserVoiceChange={handleBrowserVoiceChange}
            isSpanish={isSpanish}
            rate={rate}
            onRateChange={setRate}
            humanAudioAvailable={humanAudioAvailable}
            isSignedIn={!!user}
            es={uiEs}
          />

          {humanAudioAvailable && (
            <Button
              onClick={handleSaveOffline}
              variant="outline"
              className="w-full"
              disabled={offlineState === 'saving' || offlineState === 'saved'}
              data-testid="audio-offline-save-btn"
            >
              {offlineState === 'saved' ? (
                <><Check className="w-4 h-4 mr-2" /> {uiEs ? 'Guardado sin conexión' : 'Saved for offline'}</>
              ) : offlineState === 'saving' ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {uiEs ? 'Guardando…' : 'Saving…'}</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> {uiEs ? 'Guardar audio del capítulo sin conexión' : 'Save chapter audio for offline'}</>
              )}
            </Button>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => setShareOpen(true)}
              variant="outline"
              className="w-full"
              data-testid="audio-share-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {uiEs ? 'Compartir momento' : 'Share moment'}
            </Button>
            <Button onClick={onClose} variant="ghost" className="w-full" data-testid="audio-close-btn">
              {uiEs ? 'Cerrar' : 'Close'}
            </Button>
          </div>
        </div>
      </DialogContent>

      <ShareMomentDialog
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        verse={activeVerse}
        initialScene={scenePlaying ? sceneId : 'lumera'}
      />
    </Dialog>
  );
};

export default AudioBiblePlayer;
